<template>
  <div
    v-if="!hidden && !appliedCouponCode && addOn && defaultCouponCode"
    class="group -middle -full -bg-07 -vs-small -hs-medium -flex -between"
  >
    <!-- coupon not applied -->
    <div class="bag-item -flex">
      <div class="item-image">
        <div class="content-thumbnail-holder">
          <a class="no-hover" :href="addOn.url" :title="`View ${addOn.title}`">
            <card-src-set-image
              :img-prefix="imageUrlPrefix"
              :image="addOn.image"
              :title="addOn.title"
              type="thumb-130"
            />
          </a>
        </div>
      </div>
      <div class="item-info">
        <div class="item-details">Add and Save!</div>
        <h1 class="item-title">
          Add {{ addOn.short_name }} to your order and get
          <strong>{{ defaultCouponDiscount }}</strong>
        </h1>
        <form
          ref="couponForm"
          class="item-more-info"
          method="post"
          action="/store/coupon"
          @submit.prevent="handleApplyCoupon"
        >
          <csrf-input />
          <input type="hidden" name="itemType" value="plan" />
          <input type="hidden" name="addToCart" :value="addOn.id" />
          <input type="hidden" name="code" :value="defaultCouponCode.code" />
          <div class="btn-cluster">
            <submit-button
              type="button"
              classes="-medium -link"
              :show-icon="false"
              label="No Thanks"
              :submitting="processing"
              submitting-label="No Thanks"
              @submit-action="handleHideCoupon"
            />
            <submit-button
              type="submit"
              classes="-medium -main"
              label="Add Now"
              :submitting="processing"
              submitting-label="Add Now"
              @submit-action="handleApplyCoupon"
            />
          </div>
        </form>
      </div>
    </div>
    <div class="bag-action">
      <icon-button
        classes="-ico-only -no-container"
        icon="close"
        :icons-outlined="true"
        @click-action="handleHideCoupon"
      />
    </div>
    <div class="bag-price -item-price -inactive">
      <span class="normal-price -strike">{{ numberFormat(addOn.price) }}</span>
      <span class="sale-price">{{ priceAfterDiscount }}</span>
    </div>
  </div>
  <div v-else-if="appliedCouponCode" class="group -middle -full -bg-07 -vs-small -hs-medium -flex">
    <!-- coupon applied -->
    <div class="bag-message">
      <h1 class="message-title">
        {{ appliedCouponCode.title }}
      </h1>
      <p class="message-body">{{ appliedCouponCode.description }}</p>
    </div>
  </div>
</template>

<script>
import { defineComponent } from 'vue'
import { mapState } from 'pinia'
import { useUserAuthStore } from '@/stores/user-auth'
import CsrfInput from '@/components/forms/csrf-input.vue'
import CardSrcSetImage from '@/components/images/card-src-set-image.vue'
import SubmitButton from '@/components/buttons/submit-button.vue'
import IconButton from '@/components/buttons/icon-button.vue'
import { fbApi } from '@/utils/http-api'

/**
 * The default coupon code (meal plan) for the cart page.
 *
 * If the coupon changes, this will also need to be changed
 */
export default defineComponent({
  name: 'CartCouponCodeForm',
  components: {
    CsrfInput,
    CardSrcSetImage,
    SubmitButton,
    IconButton,
  },
  props: {
    // cart coupon models
    appliedCouponCode: {
      type: Object,
      default: () => null,
    },
    defaultCouponCode: {
      type: Object,
      default: () => null,
    },

    // plan model
    addOn: {
      type: Object,
      default: () => null,
    },
  },
  data() {
    return {
      hidden: false,
      processing: false,
    }
  },
  computed: {
    ...mapState(useUserAuthStore, ['cdn']),

    imageUrlPrefix() {
      // get siteassets url from page load, allows CDN url to be added
      const siteAssetsPrefix = this.cdn.siteAssetsPrefix || '/siteassets'
      return siteAssetsPrefix + '/plan'
    },

    defaultCouponDiscount() {
      // dollar off
      if (this.defaultCouponCode.type === 'cart_dollar_off') {
        const formattedDiscount = this.numberFormat(this.defaultCouponCode.discount)
        return `${formattedDiscount} Off`
      }

      // percent off
      if (this.defaultCouponCode.type === 'cart_percent_off') {
        return `${Math.round(Number.parseFloat(this.defaultCouponCode.discount))}% Off`
      }

      // default
      return ''
    },

    priceAfterDiscount() {
      const price = Number.parseFloat(this.addOn.price)
      let discount = Number.parseFloat(this.defaultCouponCode.discount)

      // percent off
      if (this.defaultCouponCode.type === 'cart_percent_off') {
        discount = price * (discount / 100)
        return this.numberFormat(price - discount)
      }

      // default
      return this.numberFormat(price - discount)
    },
  },

  // instance methods
  methods: {
    numberFormat(val) {
      return Number.parseFloat(val).toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
      })
    },

    // no thanks or X click, instantly hide and no error message
    async handleHideCoupon() {
      console.log('handleHideCoupon')
      this.hidden = true

      try {
        await fbApi.put(`/store/coupon/hide`, {
          json: {
            code: this.defaultCouponCode.code,
          },
        })
      } catch (err) {
        // not handling error
      }
    },

    // submit the form, no error handler
    async handleApplyCoupon() {
      this.processing = true
      this.$refs.couponForm.submit()
    },
  },
})
</script>
